// Shared solid contacts and random companions. R7 contacts emit mood events.
// Pure simulation: no renderer, DOM, timers or frame-count dependent rules.
import {advanceGuided} from './chapter-dynamics.js';
import {AUTO_TILT_LIMIT,limitTilt} from './interaction-tuning.js';
import {applyMoodEvent} from './mood-system.js';
export const STORY_ORDER = ['paths','relationships','emotions','discomfort','resilience','walk'];
export const EXPERIENCE_MODES = new Set(['emotions','discomfort','resilience']);
export const clamp01 = n => Math.max(0, Math.min(1, n));
const clamp = (n,a,b) => Math.max(a,Math.min(b,n));
const smooth = (a,b,x) => {const t=clamp01((x-a)/(b-a));return t*t*(3-2*t);};
const approach = (a,b,rate,dt) => a+(b-a)*(1-Math.exp(-rate*dt));
export function quicksandAt(x,z){
 const a=Math.hypot((x+1.12)/1.16,(z-.75)/1.02);
 const b=Math.hypot((x-1.50)/.91,(z+1.35)/1.12);
 return Math.max(1-smooth(.48,1.04,a),1-smooth(.48,1.04,b));
}
export function unstableTilt(t){
 return limitTilt(.23*Math.sin(t*.72)+.085*Math.sin(t*.23+1.2)+.035*Math.sin(t*1.31),
  .24*Math.sin(t*.58+.7)+.085*Math.cos(t*.31)+.025*Math.sin(t*1.07),AUTO_TILT_LIMIT);
}
// Position projection plus a low-restitution impulse. Both bodies move: the
// pointer-controlled ball is never an infinite-mass kinematic obstacle.
export function resolvePair(a,b,restitution=.16){
 let dx=b.x-a.x,dz=b.z-a.z,d=Math.hypot(dx,dz),r=a.radius+b.radius;
 if(d>=r)return false;
 if(d<1e-8){dx=1;dz=0;d=1;}
 const nx=dx/d,nz=dz/d;
 const actual=Math.hypot(b.x-a.x,b.z-a.z),penetration=r-actual+.00002;
 const ia=1/(a.mass||1),ib=1/(b.mass||1),total=ia+ib;
 a.x-=nx*penetration*ia/total;a.z-=nz*penetration*ia/total;
 b.x+=nx*penetration*ib/total;b.z+=nz*penetration*ib/total;
 const relative=(b.vx-a.vx)*nx+(b.vz-a.vz)*nz;
 if(relative<0){const j=-(1+restitution)*relative/total;
  a.vx-=j*nx*ia;a.vz-=j*nz*ia;b.vx+=j*nx*ib;b.vz+=j*nz*ib;
 }
 return true;
}
export function keepInBounds(b,edge=3.69){
 const bound=edge-b.radius;
 if(b.x>bound){b.x=bound;if(b.vx>0)b.vx*=-.15;}
 if(b.x<-bound){b.x=-bound;if(b.vx<0)b.vx*=-.15;}
 if(b.z>bound){b.z=bound;if(b.vz>0)b.vz*=-.15;}
 if(b.z<-bound){b.z=-bound;if(b.vz<0)b.vz*=-.15;}
}
export function solveBallContacts(bodies){
 let contacts=0;
 for(let iteration=0;iteration<10;iteration++){
  bodies.forEach(b=>keepInBounds(b));
  for(let i=0;i<bodies.length;i++)for(let j=i+1;j<bodies.length;j++)
   if(resolvePair(bodies[i],bodies[j]))contacts++;
 }
 bodies.forEach(b=>keepInBounds(b));return contacts;
}
export class RelationshipSystem {
 constructor(random=Math.random){
  this.random=random;this.partner=null;this.role=null;this.heading={x:1,z:0};this.joinBlend=0;this.time=0;this.collisions=0;
  this.peers=[[-1.0,.28],[1.8,-1.5],[-2.2,-2]].map(([x,z],id)=>({id,x,z,vx:0,vz:0,radius:.195,mass:1,scale:1,tx:x,tz:z,next:0,hx:1,hz:0}));
 }
 reset(){Object.assign(this,new RelationshipSystem(this.random));}
 nearby(player,limit=1.35){
  let found=null,best=limit;
  for(const p of this.peers){const d=Math.hypot(player.x-p.x,player.z-p.z);if(d<best){best=d;found=p.id;}}
  return found;
 }
 get isFollowing(){return this.partner!==null&&this.role==='follow';}
 join(id,player,role='follow'){
  const p=this.peers[id];if(!p||!['lead','follow'].includes(role)||this.partner!==null||Math.hypot(p.x-player.x,p.z-player.z)>1.65)return false;
  this.partner=id;this.role=role;const length=Math.hypot(player.x-p.x,player.z-p.z)||1;this.heading={x:(player.x-p.x)/length,z:(player.z-p.z)/length};p.next=0;return true;
 }
 separate(){this.partner=null;this.role=null;}
 leave(){this.separate();this.joinBlend=0;for(const p of this.peers){p.scale=1;p.radius=.195;p.mass=1;}}
 step(player,dt,{aim=null,feeling}={}){
  const steps=Math.max(1,Math.ceil(dt*120)),h=dt/steps;
  for(let n=0;n<steps;n++){
   this.time+=h;this.joinBlend=approach(this.joinBlend,this.partner===null?0:1,5.5,h);
   player.radius=.225*(feeling?.sx||1);player.mass=feeling?.mass||1;
   for(const p of this.peers){
    p.scale=1;p.radius=.195;p.mass=1;
    if(this.partner===p.id&&this.role==='lead')continue;
    if(this.time>=p.next||Math.hypot(p.tx-p.x,p.tz-p.z)<.24){
     let tries=0;do{p.tx=(this.random()*2-1)*2.95;p.tz=(this.random()*2-1)*2.95;}while(Math.hypot(p.tx-p.x,p.tz-p.z)<1.1&&++tries<10);
     p.next=this.time+2+this.random()*3.5;
    }
    const dx=p.tx-p.x,dz=p.tz-p.z,d=Math.hypot(dx,dz)||1;
    const speed=this.partner===p.id?(feeling?.maxSpeed||2.6)*.80:.65+p.id*.10;
    const cap=Math.min(speed,d*3);
    p.vx=approach(p.vx,dx/d*cap,this.partner===p.id?3.3:2.0,h);
    p.vz=approach(p.vz,dz/d*cap,this.partner===p.id?3.3:2.0,h);
    p.x+=p.vx*h;p.z+=p.vz*h;
    const v=Math.hypot(p.vx,p.vz);if(v>.04){p.hx=approach(p.hx,p.vx/v,3,h);p.hz=approach(p.hz,p.vz/v,3,h);}
   }
   if(this.isFollowing){
    const p=this.peers[this.partner],length=Math.hypot(p.hx,p.hz)||1,hx=p.hx/length,hz=p.hz/length;
    const gap=player.radius+p.radius+.09;
    // Soft trailing formation, not overlapping positions or a welded rigid body.
    const tx=p.x-hx*gap,tz=p.z-hz*gap;
    const dx=tx-player.x,dz=tz-player.z;
    let vx=p.vx+dx*7,vz=p.vz+dz*7;const speed=Math.hypot(vx,vz),cap=(feeling?.maxSpeed||2.6)*1.12;
    if(speed>cap){vx*=cap/speed;vz*=cap/speed;}
    player.vx=approach(player.vx,vx,11,h);player.vz=approach(player.vz,vz,11,h);
    player.x+=player.vx*h;player.z+=player.vz*h;
   }else if(aim){advanceGuided(player,aim,h,feeling,{mode:'relationships'});}
   else{const damping=Math.exp(-(feeling?.damping||9.5)*h);player.vx*=damping;player.vz*=damping;player.x+=player.vx*h;player.z+=player.vz*h;}
   if(this.partner!==null&&this.role==='lead'){
    const p=this.peers[this.partner],speed=Math.hypot(player.vx,player.vz);
    if(speed>.04){this.heading.x=approach(this.heading.x,player.vx/speed,5,h);this.heading.z=approach(this.heading.z,player.vz/speed,5,h);}
    const length=Math.hypot(this.heading.x,this.heading.z)||1,gap=player.radius+p.radius+.09;
    const dx=player.x-this.heading.x/length*gap-p.x,dz=player.z-this.heading.z/length*gap-p.z;
    let vx=player.vx+dx*7,vz=player.vz+dz*7;const v=Math.hypot(vx,vz),cap=(feeling?.maxSpeed||2.6)*1.25;
    if(v>cap){vx*=cap/v;vz*=cap/v;}
    p.vx=approach(p.vx,vx,11,h);p.vz=approach(p.vz,vz,11,h);p.x+=p.vx*h;p.z+=p.vz*h;
    if(Math.hypot(p.vx,p.vz)>.04){p.hx=this.heading.x/length;p.hz=this.heading.z/length;}
   }
   this.collisions+=solveBallContacts([player,...this.peers]);
  }
  return player;
 }
}
export class RisingRocks {
 constructor(random=Math.random){this.random=random;this.time=0;this.blend=0;this.rocks=[];this.nextId=0;this.spawnClock=0;this.hitCount=0;this.impactEvents=[];}
 reset(){Object.assign(this,new RisingRocks(this.random));}
 spawn(player){
  for(let attempt=0;attempt<32;attempt++){
   const x=(this.random()*2-1)*2.85,z=(this.random()*2-1)*2.85,radius=.26+this.random()*.14;
   if(Math.hypot(x-player.x,z-player.z)<radius+(player.radius||.3)+.42)continue;
   if(this.rocks.some(r=>Math.hypot(x-r.x,z-r.z)<radius+r.baseRadius+.45))continue;
   this.rocks.push({id:this.nextId++,x,z,baseRadius:radius,radius:0,rise:0,age:0,lifetime:12+this.random()*8,touching:false,lastHit:-100});return;
  }
 }
 step(dt,mode,player){
  const enabled=mode==='emotions'||mode==='resilience';this.time+=dt;
  this.blend=approach(this.blend,enabled?1:0,3,dt);
  if(enabled){
   this.spawnClock-=dt;
   if(this.rocks.length<4&&this.spawnClock<=0){this.spawn(player);this.spawnClock=this.rocks.length<2?.65:2.4+this.random()*2.4;}
   for(const r of this.rocks)r.age+=dt;
  }
  this.rocks=this.rocks.filter(r=>r.age<r.lifetime);
  for(const r of this.rocks){r.rise=smooth(0,1.5,r.age)*(1-smooth(r.lifetime-1.8,r.lifetime,r.age))*this.blend;r.radius=r.baseRadius*r.rise;}
 }
 takeImpacts(){const events=this.impactEvents.splice(0);return events;}
 collide(player,mood,mode){
  if(mode!=='emotions'&&mode!=='resilience')return 0;
  let impacts=0;
  // A few solver passes prevent squeezing into a pair of nearby stones.
  for(let pass=0;pass<3;pass++)for(const r of this.rocks){
   if(r.rise<.14){r.touching=false;continue;}
   let dx=player.x-r.x,dz=player.z-r.z,d=Math.hypot(dx,dz),rr=player.radius+r.radius;
   if(d>rr+.045){r.touching=false;continue;}
   if(d>=rr)continue;
   if(d<1e-8){dx=1;dz=0;d=1;}
   const nx=dx/d,nz=dz/d,impact=Math.max(0,-player.vx*nx-player.vz*nz);
   player.x=r.x+nx*(rr+.00005);player.z=r.z+nz*(rr+.00005);
   if(impact>0){const rebound=clamp(.48+impact*.60,.55,2.2);player.vx+=nx*(impact+rebound);player.vz+=nz*(impact+rebound);if(impact>.10)player.recoil=Math.max(player.recoil||0,.18);}
   if(!r.touching&&impact>.10&&this.time-r.lastHit>.9){
    applyMoodEvent(mood,'stone');r.lastHit=this.time;impacts++;this.hitCount++;this.impactEvents.push({type:'stone',x:r.x+nx*r.radius,z:r.z+nz*r.radius,nx,nz,strength:clamp(.35+impact*.22,.35,1)});
   }
   r.touching=true;
  }
  keepInBounds(player);return impacts;
 }
}
